import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { GsdReadableBlock, GsdStandaloneModuleDashboard, useI18n } from "@/coquille-bridge";
import { useDataStore } from "@/components/admin/useDataStore.js";
import {
  InternalReferentialNotice,
  TerminologyPanel,
} from "@/components/titles/KarateTitleRecognitionPanels.jsx";
import {
  ADMIN_SHOTOKAN_BASE,
  ADMIN_SHOTOKAN_SUB_NAV,
  SHOTOKAN_MODULE_EN,
  SHOTOKAN_MODULE_FR,
} from "@/pages/shotokanBlackDragon/shotokanNavConfig.js";
import { SHOTOKAN_DOCUMENTS_PREUVES_BASE } from "@/pages/documentsPreuves/documentsPreuvesNavConfig.js";

const ACTIVE_APP_STATUSES = new Set([
  "soumis",
  "en_revision",
  "documents_manquants",
  "recommande",
  "en_attente_documents",
]);

function useLocale() {
  const { lang } = useI18n();
  return lang === "en" ? "en" : "fr";
}

function useDashboardStats() {
  const store = useDataStore();
  return useMemo(() => {
    const titles = store.martialTitles || [];
    const applications = store.titleApplications || [];
    const register = store.titleRegister || [];
    const documents = store.authorityDocuments || [];
    const dossiers = store.evidenceDossiers || [];
    const journal = store.validationLog || [];

    const appsInProgress = applications.filter((a) => ACTIVE_APP_STATUSES.has(a.statut)).length;
    const docsToProvide = documents.filter((d) => d.statut_validation === "a_fournir").length;
    const incompleteDossiers = dossiers.filter(
      (d) => d.statut_dossier === "incomplet" || (d.score_completude ?? 100) < 100,
    ).length;

    const lastLog = [...journal].sort((a, b) => (b.date || "").localeCompare(a.date || ""))[0];

    return {
      titleCount: titles.length,
      appsInProgress,
      recognitionCount: register.length,
      docsToProvide,
      incompleteDossiers,
      lastLog,
    };
  }, [store]);
}

function QuickActionLink({ to, label, testId }) {
  return (
    <Link to={to} className="gsd-quick-actions__btn" data-testid={testId}>
      {label}
    </Link>
  );
}

/** Tableau de bord admin — pattern coquille GsdStandaloneModuleDashboard + contenu métier Karaté */
export function ShotokanBlackDragonAdminDashboard() {
  const locale = useLocale();
  const stats = useDashboardStats();
  const base = ADMIN_SHOTOKAN_BASE;

  const quickLinks = [
    { segment: "titres", labelFr: "Voir les titres", labelEn: "View titles", testId: "sbd-quick-titres" },
    { segment: "demandes", labelFr: "Voir les demandes", labelEn: "View requests", testId: "sbd-quick-demandes" },
    { segment: "autorite", labelFr: "Voir l'autorité", labelEn: "View authority", testId: "sbd-quick-autorite" },
    { segment: "documents", labelFr: "Voir les documents", labelEn: "View documents", testId: "sbd-quick-documents" },
    { segment: "dossiers-preuves", labelFr: "Voir les dossiers", labelEn: "View dossiers", testId: "sbd-quick-dossiers" },
    { segment: "journal-validation", labelFr: "Voir le journal", labelEn: "View journal", testId: "sbd-quick-journal" },
  ];

  return (
    <div data-testid="page-admin-shotokan-black-dragon">
      <GsdStandaloneModuleDashboard
        title={{ labelFr: SHOTOKAN_MODULE_FR, labelEn: SHOTOKAN_MODULE_EN }}
        subtitle={{
          labelFr: "Titres, reconnaissances et documents d'autorité",
          labelEn: "Titles, recognitions and authority documents",
        }}
        icon="🐉"
        lang={locale}
        testId="sbd-coquille-dashboard"
        summaryCards={[
          { id: "titles", label: locale === "en" ? "Titles" : "Titres", value: stats.titleCount, testId: "sbd-stat-titles" },
          { id: "applications", label: locale === "en" ? "In progress" : "Demandes en cours", value: stats.appsInProgress },
          { id: "recognitions", label: locale === "en" ? "Recognitions" : "Reconnaissances", value: stats.recognitionCount },
          { id: "docs", label: locale === "en" ? "To provide" : "Docs à fournir", value: stats.docsToProvide },
          { id: "dossiers", label: locale === "en" ? "Incomplete" : "Dossiers incomplets", value: stats.incompleteDossiers },
        ]}
        quickActions={quickLinks.map((link) => ({
          id: link.testId,
          label: locale === "en" ? link.labelEn : link.labelFr,
          to: `${base}/${link.segment}`,
        }))}
        renderQuickAction={(action) => (
          <QuickActionLink key={action.id} to={action.to} label={action.label} testId={action.id} />
        )}
        infoBlocks={
          <>
            <InternalReferentialNotice />
            <TerminologyPanel />
            <GsdReadableBlock testId="sbd-dashboard-sections-list">
              <p className="muted">
                {locale === "en" ? "Module sections" : "Sections du module"} :{" "}
                {ADMIN_SHOTOKAN_SUB_NAV.map((n) => (locale === "en" ? n.labelEn : n.labelFr)).join(" · ")}
              </p>
              <p data-testid="sbd-link-central-documents">
                <Link to={SHOTOKAN_DOCUMENTS_PREUVES_BASE}>
                  {locale === "en"
                    ? "View all documents in Documents & Evidence"
                    : "Voir tous les documents dans Documents & Preuves"}
                </Link>
              </p>
              {stats.lastLog ? (
                <p data-testid="sbd-stat-last-log" className="muted">
                  {locale === "en" ? "Last journal action" : "Dernière action du journal"} :{" "}
                  <strong>{stats.lastLog.action}</strong> — {stats.lastLog.date}
                </p>
              ) : null}
            </GsdReadableBlock>
          </>
        }
      />
    </div>
  );
}

/** Vue d'ensemble Sensei — dashboard coquille + métier Karaté */
export function ShotokanBlackDragonSenseiDashboard() {
  const locale = useLocale();
  const stats = useDashboardStats();
  const base = "/sensei/shotokan-black-dragon";

  return (
    <div data-testid="page-sensei-shotokan-black-dragon">
      <GsdStandaloneModuleDashboard
        title={{ labelFr: SHOTOKAN_MODULE_FR, labelEn: SHOTOKAN_MODULE_EN }}
        subtitle={{
          labelFr: "Titres et reconnaissances — espace Sensei",
          labelEn: "Titles and recognitions — Sensei space",
        }}
        lang={locale}
        testId="sbd-sensei-coquille-dashboard"
        summaryCards={[
          { id: "apps", label: locale === "en" ? "In progress (demo)" : "Demandes en cours (démo)", value: stats.appsInProgress },
          { id: "docs", label: locale === "en" ? "To provide" : "Docs à fournir", value: stats.docsToProvide },
        ]}
        quickActions={[
          { id: "titres", label: locale === "en" ? "View titles" : "Voir les titres", to: `${base}/titres` },
          { id: "demandes", label: locale === "en" ? "My requests" : "Mes demandes", to: `${base}/demandes` },
          { id: "documents", label: locale === "en" ? "Required documents" : "Documents requis", to: `${base}/documents` },
        ]}
        renderQuickAction={(action) => (
          <QuickActionLink key={action.id} to={action.to} label={action.label} testId={`sbd-sensei-quick-${action.id}`} />
        )}
        infoBlocks={<InternalReferentialNotice />}
      />
    </div>
  );
}

/** Vue d'ensemble Sempai */
export function ShotokanBlackDragonSempaiDashboard() {
  const locale = useLocale();
  const base = "/sempai/shotokan-black-dragon";

  return (
    <div data-testid="page-sempai-shotokan-black-dragon">
      <GsdStandaloneModuleDashboard
        title={{ labelFr: SHOTOKAN_MODULE_FR, labelEn: SHOTOKAN_MODULE_EN }}
        subtitle={{
          labelFr: "Compréhension des titres — pas de droit de validation",
          labelEn: "Understanding titles — no validation rights",
        }}
        lang={locale}
        testId="sbd-sempai-coquille-dashboard"
        quickActions={[
          { id: "parcours", label: locale === "en" ? "Title path" : "Parcours titres", to: `${base}/parcours` },
          { id: "documents", label: locale === "en" ? "Required documents" : "Documents requis", to: `${base}/documents` },
        ]}
        renderQuickAction={(action) => (
          <QuickActionLink key={action.id} to={action.to} label={action.label} testId={`sbd-sempai-quick-${action.id}`} />
        )}
        infoBlocks={
          <>
            <InternalReferentialNotice />
            <GsdReadableBlock testId="sbd-sempai-dashboard-links">
              <p className="muted">
                {locale === "en"
                  ? "Consult the title path and required proofs — demo only."
                  : "Consultez le parcours des titres et les preuves requises — démo seulement."}
              </p>
            </GsdReadableBlock>
          </>
        }
      />
    </div>
  );
}
